<template>
  <div id="extrusion-panel-container" class="operator-page">
    <!-- Header Component (Standard SFC Operator Header) -->
    <AppointmentHeader
      :is-point-resource-page="true"
      :is-fixed="false"
      :resource-name="headerTitleText"
      :status="statusBadgeText"
      :color="statusBadgeColor"
      :back-event="goBack"
      :order-object="panelData"
    >
      <template #actions>
        <div class="extrusion-header-actions">
          <!-- 1. Visualizar mais informações da ordem -->
          <button
            id="btn-extrusion-order-details"
            type="button"
            class="btn-extrusion-header-action"
            :class="{ 'btn-disabled': isUnstarted && !orderCode }"
            :disabled="isUnstarted && !orderCode"
            title="Visualizar detalhes da ordem"
            aria-label="Visualizar detalhes da ordem"
            @click="openOrderDetailsModal"
          >
            <ShowDetailsIcon class="header-action-svg icon-order-details" />
          </button>
          <b-tooltip
            target="btn-extrusion-order-details"
            triggers="hover focus"
            placement="bottom"
            custom-class="tooltip-extrusion-header"
          >
            Visualizar detalhes da ordem
          </b-tooltip>

          <!-- 2. Iniciar / Selecionar nova OP direto na tela da ordem -->
          <button
            id="btn-extrusion-new-order"
            type="button"
            class="btn-extrusion-header-action"
            title="Iniciar nova OP / Selecionar ordem"
            aria-label="Iniciar nova OP / Selecionar ordem"
            @click="openSelectNewOrderModal"
          >
            <AddCircleIcon class="header-action-svg" />
          </button>
          <b-tooltip
            target="btn-extrusion-new-order"
            triggers="hover focus"
            placement="bottom"
            custom-class="tooltip-extrusion-header"
          >
            Iniciar nova OP / Selecionar ordem
          </b-tooltip>
        </div>
      </template>
    </AppointmentHeader>

    <!-- Main Content Area (Calculated viewport height) -->
    <div class="operator-body">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <b-spinner variant="warning" size="md" label="Carregando..." />
        <span class="ml-1 font-raleway font-weight-bold text-dark">Carregando Apontamentos da Extrusão...</span>
      </div>

      <!-- Main Content Panel (Unified Operator Layout + Extrusion Fields) -->
      <div v-else class="panel-layout-grid">
        <!-- ACCORDION HEADER FOR TABLET/MOBILE (< 992px) -->
        <div class="metrics-accordion-header" @click="isMetricsExpanded = !isMetricsExpanded">
          <div class="d-flex align-items-center">
            <feather-icon icon="BarChart2Icon" size="18" class="mr-50 text-warning" />
            <span class="font-raleway font-weight-bold text-dark">Métricas & Especificações da OP</span>
            <span class="badge badge-light-secondary ml-75 font-weight-bold" v-if="orderCode">#{{ orderCode }}</span>
          </div>
          <div class="d-flex align-items-center text-muted small">
            <span class="mr-50 font-raleway">{{ isMetricsExpanded ? 'Ocultar' : 'Ver detalhes' }}</span>
            <feather-icon :icon="isMetricsExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="18" />
          </div>
        </div>

        <!-- PASSIVE METRICS WRAPPER (ROW 1, 2, 3) -->
        <div class="passive-metrics-wrapper" v-show="!isMobileScreen || isMetricsExpanded">
          <!-- ROW 1: Header Specs Grid (8 Cards + Action Buttons) -->
          <div class="specs-grid-container">
            <!-- 1. Ordem -->
            <div class="spec-card">
              <div class="spec-card-title">Ordem</div>
              <div class="spec-card-value font-weight-bold">
                {{ isUnstarted ? '-' : `#${activePanelData.orderCode}` }}
              </div>
              <div class="spec-card-origin tag-op">Origem: OP</div>
            </div>

            <!-- 2. Material (Wider) -->
            <div class="spec-card spec-card-material">
              <div class="spec-card-title">Material</div>
              <div class="spec-card-value font-weight-bold text-truncate" :title="activePanelData.material">
                {{ isUnstarted ? '-' : (activePanelData.material || '-') }}
              </div>
              <div class="spec-card-origin tag-op">Origem: OP</div>
            </div>

            <!-- 3. Matriz -->
            <div class="spec-card">
              <div class="spec-card-title">Matriz</div>
              <div class="spec-card-value">{{ isUnstarted ? '-' : (activePanelData.matrix || '03') }}</div>
              <div class="spec-card-origin tag-op">Origem: OP</div>
            </div>

            <!-- 4. Liga -->
            <div class="spec-card">
              <div class="spec-card-title">Liga</div>
              <div class="spec-card-value">{{ isUnstarted ? '-' : (activePanelData.alloy || '6063') }}</div>
              <div class="spec-card-origin tag-product">Origem: Produto</div>
            </div>

            <!-- 5. Têmpera -->
            <div class="spec-card">
              <div class="spec-card-title">Têmpera</div>
              <div class="spec-card-value">{{ isUnstarted ? '-' : (activePanelData.temper || 'T5') }}</div>
              <div class="spec-card-origin tag-product">Origem: Produto</div>
            </div>

            <!-- 6. Vel. extrusão -->
            <div class="spec-card">
              <div class="spec-card-title">Vel. extrusão</div>
              <div class="spec-card-value">{{ isUnstarted ? '0' : formatNumber(activePanelData.extrusionSpeedTarget) }} m/min</div>
              <div class="spec-card-origin tag-product">Origem: Produto</div>
            </div>

            <!-- 7. Kg/h meta -->
            <div class="spec-card">
              <div class="spec-card-title">Kg/h meta</div>
              <div class="spec-card-value">{{ isUnstarted ? '0' : formatInt(activePanelData.kghTarget) }}</div>
              <div class="spec-card-origin tag-product">Origem: Produto</div>
            </div>

            <!-- 8. Efic. meta -->
            <div class="spec-card">
              <div class="spec-card-title">Efic. meta</div>
              <div class="spec-card-value text-success font-weight-bold">{{ isUnstarted ? '0%' : formatPercent(activePanelData.efficiencyTarget) }}</div>
              <div class="spec-card-origin tag-product">Origem: Produto</div>
            </div>

            <!-- Campos Extras Dinâmicos (Qualquer novo campo adicionado no JSON Template) -->
            <div v-for="field in dynamicExtraCustomFields" :key="field.key" class="spec-card">
              <div class="spec-card-title">{{ field.title }}</div>
              <div class="spec-card-value">{{ field.value }}</div>
              <div class="spec-card-origin" :class="field.tagClass">{{ field.origin }}</div>
            </div>
          </div>

          <!-- ROW 2: Data Origin Legend Bar -->
          <div class="legend-bar">
            <span class="legend-label">Origem dos campos:</span>
            <span class="legend-item tag-op"><strong>OP</strong> = ordem de produção</span>
            <span class="legend-sep">|</span>
            <span class="legend-item tag-product"><strong>Produto</strong> = cadastro/padrão do produto</span>
            <span class="legend-sep">|</span>
            <span class="legend-item tag-extrusion"><strong>Extrusão</strong> = apontamento da extrusão</span>
            <span class="legend-sep">|</span>
            <span class="legend-item tag-cut"><strong>Corte</strong> = apontamento do corte</span>
            <span class="legend-sep">|</span>
            <span class="legend-item tag-calc"><strong>Calculado</strong> = regra de cálculo do sistema/backend</span>
          </div>

          <!-- ROW 3: 11 KPI Metrics Grid -->
          <div class="metrics-grid">
            <!-- 1. Total kg -->
            <div class="metric-card">
              <div class="metric-title">Total kg</div>
              <div class="metric-value">{{ isUnstarted ? '0' : formatInt(activePanelData.totalKg) }}</div>
              <div class="metric-origin tag-op">Origem: OP</div>
            </div>

            <!-- 2. Qtd. barras -->
            <div class="metric-card">
              <div class="metric-title">Qtd. barras</div>
              <div class="metric-value">{{ isUnstarted ? '0' : formatInt(activePanelData.barQuantity) }}</div>
              <div class="metric-origin tag-op">Origem: OP</div>
            </div>

            <!-- 3. Tam. barra -->
            <div class="metric-card">
              <div class="metric-title">Tam. barra (mm)</div>
              <div class="metric-value">{{ isUnstarted ? '0' : formatInt(activePanelData.barSizeMm) }}</div>
              <div class="metric-origin tag-op">Origem: OP</div>
            </div>

            <!-- 4. Kg/h -->
            <div class="metric-card">
              <div class="metric-title">Kg/h</div>
              <div class="metric-value">{{ isUnstarted ? '0' : formatInt(activePanelData.kghCalculated) }}</div>
              <div class="metric-origin tag-calc">Origem: Calculado</div>
            </div>

            <!-- 5. Performance -->
            <div class="metric-card">
              <div class="metric-title">Performance</div>
              <div class="metric-value text-orange-highlight">{{ isUnstarted ? '0%' : formatPercentValue(activePanelData.performancePercentage) }}</div>
              <div class="metric-origin tag-calc">Origem: Calculado</div>
            </div>

            <!-- 6. Prod. (kg) -->
            <div class="metric-card">
              <div class="metric-title">Prod. (kg)</div>
              <div class="metric-value">{{ isUnstarted ? '0' : formatInt(activePanelData.producedKg) }}</div>
              <div class="metric-origin tag-extrusion">Origem: Extrusão</div>
            </div>

            <!-- 7. Prod. (barra) -->
            <div class="metric-card">
              <div class="metric-title">Prod. (barra)</div>
              <div class="metric-value">{{ isUnstarted ? '0' : formatInt(activePanelData.producedBars) }}</div>
              <div class="metric-origin tag-extrusion">Origem: Extrusão</div>
            </div>

            <!-- 8. Refugo -->
            <div class="metric-card">
              <div class="metric-title">Refugo</div>
              <div
                class="metric-value"
                :title="activePanelData.totalCuttingRefuseKg > 0 ? `${formatInt(activePanelData.scrapKg)} barras (${activePanelData.totalCuttingRefuseKg} kg)` : ''"
              >
                {{ isUnstarted ? '-' : (activePanelData.scrapKg !== null && activePanelData.scrapKg !== undefined ? formatInt(activePanelData.scrapKg) : '0') }}
              </div>
              <div class="metric-origin tag-cut">Origem: Corte</div>
            </div>

            <!-- 9. Produzido -->
            <div class="metric-card">
              <div class="metric-title">Produzido</div>
              <div class="metric-value text-orange-highlight">{{ isUnstarted ? '0%' : formatPercentValue(activePanelData.producedPercentage) }}</div>
              <div class="metric-origin tag-calc">Origem: Calculado</div>
            </div>

            <!-- 10. Qualidade -->
            <div class="metric-card">
              <div class="metric-title">Qualidade</div>
              <div class="metric-value">{{ isUnstarted ? '0.0%' : (activePanelData.qualityPercentage !== null && activePanelData.qualityPercentage !== undefined ? formatPercentValue(activePanelData.qualityPercentage) : '100%') }}</div>
              <div class="metric-origin tag-calc">Origem: Calculado</div>
            </div>

            <!-- 11. Efic. prensa -->
            <div class="metric-card">
              <div class="metric-title">Efic. prensa</div>
              <div class="metric-value">{{ isUnstarted ? '0%' : formatPercentValue(activePanelData.pressEfficiencyPercentage) }}</div>
              <div class="metric-origin tag-calc">Origem: Calculado</div>
            </div>
          </div>
        </div>

        <!-- CUTTING TRANSITION BANNER (When in Cutting step and active corte) -->
        <div v-if="isCuttingStep && currentStatus === 'corte' && !isUnstarted" class="cutting-banner">
          <div class="cutting-banner-left">
            <div class="cutting-pulse-dot"></div>
            <div>
              <div class="cutting-banner-title font-raleway font-weight-bolder">
                Aguardando processo de corte
              </div>
              <div class="cutting-banner-subtitle">
                Tempo de folga controlado pelo APS. Realize o apontamento unificado de cestos e refugos do corte.
              </div>
            </div>
          </div>
        </div>

        <!-- ROW 4: Operator Activity Cards -->
        <div class="activity-cards-grid" :class="{ 'unstarted-cards-grid': isUnstarted, 'cutting-cards-grid': isCuttingStep && currentStatus !== 'finished', 'finished-cards-grid': currentStatus === 'finished' }">
          <!-- Active Finished Card (When order/cutting is finished) -->
          <div
            v-if="currentStatus === 'finished'"
            class="activity-card card-active-green cursor-default d-flex flex-column align-items-center justify-content-center"
            style="grid-column: 1 / -1; width: 100%; min-height: 180px;"
          >
            <div class="icon-square-white">
              <feather-icon icon="CheckCircleIcon" size="32" class="text-success" />
            </div>
            <div class="card-activity-title font-raleway font-weight-bold text-white text-uppercase" style="font-size: 20px; letter-spacing: 0.5px;">
              Finalizado
            </div>
            <div class="card-activity-timestamp text-white opacity-9 font-weight-bold mb-1">
              {{ statusStartedAtFormatted }}
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-light font-weight-bold px-2 py-50 mt-50"
              style="border-radius: 8px; border-width: 2px;"
              title="Estornar finalização e reabrir ordem para novos apontamentos"
              @click="promptReopenOrder"
            >
              <feather-icon icon="RotateCcwIcon" size="14" class="mr-50" />
              <span>Reabrir Ordem / Estornar</span>
            </button>
          </div>

          <template v-else>
            <!-- Card 1: Setup (Only in Extrusion Mode) -->
            <template v-if="!isCuttingStep">
              <div
                v-if="isUnstarted || currentStatus === 'unstarted'"
                class="activity-card card-unstarted"
                @click="handleCardClick('setup')"
              >
                <div class="unstarted-icon-box bg-slate">
                  <feather-icon icon="SlidersIcon" size="32" />
                </div>
                <div class="card-activity-title font-raleway font-weight-bold">Setup</div>
              </div>

              <!-- Active Setup Card -->
              <div
                v-else-if="currentStatus === 'setup'"
                class="activity-card card-active-slate cursor-pointer"
                @click="handleCardClick('setup')"
              >
                <div class="card-top-header">
                  <div class="d-flex align-items-center">
                    <span class="radio-dot mr-50"></span>
                    <span class="card-state-label font-weight-bold font-raleway">Setup</span>
                  </div>
                  <b-spinner small type="border" class="text-white opacity-8" />
                </div>

                <div class="card-timer-body text-center">
                  <div class="timer-date-stamp">{{ setupStartedAtFormatted }}</div>
                  <div class="timer-display">
                    {{ timerDisplay.hours }} : {{ timerDisplay.minutes }} : {{ timerDisplay.seconds }}
                  </div>
                  <div class="timer-labels">
                    <span>H</span>
                    <span>M</span>
                    <span>S</span>
                  </div>
                </div>
              </div>

              <!-- Inactive Setup Card -->
              <div
                v-else
                class="activity-card card-setup cursor-pointer"
                @click="handleCardClick('setup')"
              >
                <div class="icon-square icon-setup-bg">
                  <feather-icon icon="SlidersIcon" size="26" />
                </div>
                <div class="card-activity-title font-raleway font-weight-bold">Setup</div>
                <div class="card-activity-timestamp">{{ setupStartedAtFormatted }}</div>
              </div>
            </template>

            <!-- Card 2 in Cutting Mode: Apontamento do Corte (Active Purple Card) -->
            <div
              v-if="isCuttingStep && currentStatus === 'corte' && !isUnstarted"
              class="activity-card card-parcial card-active-purple cursor-pointer"
              @click="openCuttingBasketModal"
            >
              <div class="card-top-header">
                <div class="d-flex align-items-center">
                  <span class="radio-dot mr-50"></span>
                  <span class="card-state-label font-weight-bold font-raleway">Apontamento do Corte</span>
                </div>
                <feather-icon icon="BoxIcon" size="16" class="text-white opacity-8" />
              </div>

              <div class="card-timer-body text-center">
                <div class="timer-date-stamp">{{ statusStartedAtFormatted }}</div>
                <div class="timer-display">
                  {{ timerDisplay.hours }} : {{ timerDisplay.minutes }} : {{ timerDisplay.seconds }}
                </div>
                <div class="timer-labels">
                  <span>H</span>
                  <span>M</span>
                  <span>S</span>
                </div>
              </div>

              <div class="card-bottom-button" @click.stop="openCuttingBasketModal">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                <span>Apontar Corte</span>
              </div>
            </div>

            <!-- Card 2 in Extrusion Mode: Iniciar (when unstarted OR when in setup status) -->
            <div
              v-else-if="isUnstarted || currentStatus === 'unstarted' || currentStatus === 'setup'"
              class="activity-card card-unstarted cursor-pointer"
              @click="openStartOrderModal"
            >
              <div class="unstarted-icon-box bg-orange">
                <feather-icon icon="PlayCircleIcon" size="32" />
              </div>
              <div class="card-activity-title font-raleway font-weight-bold">Iniciar</div>
            </div>

            <!-- Active Parcial Card in Extrusion Mode (when execution status is active) -->
            <div
              v-else-if="currentStatus === 'parcial'"
              class="activity-card card-parcial card-active-orange cursor-pointer"
              @click="handleCardClick('tarugo')"
            >
              <div class="card-top-header">
                <div class="d-flex align-items-center">
                  <span class="radio-dot mr-50"></span>
                  <span class="card-state-label font-weight-bold font-raleway">Parcial</span>
                </div>
                <feather-icon icon="GridIcon" size="16" class="text-white opacity-8" />
              </div>

              <div class="card-timer-body text-center">
                <div class="timer-date-stamp">{{ statusStartedAtFormatted }}</div>
                <div class="timer-display">
                  {{ timerDisplay.hours }} : {{ timerDisplay.minutes }} : {{ timerDisplay.seconds }}
                </div>
                <div class="timer-labels">
                  <span>H</span>
                  <span>M</span>
                  <span>S</span>
                </div>
              </div>

              <div class="card-bottom-button" @click.stop="openTarugoModal">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                <span>+ Adicionar</span>
              </div>
            </div>

            <!-- Inactive Card 2 (Continuar when stopped/paused) -->
            <div
              v-else-if="currentStatus === 'parar'"
              class="activity-card card-setup cursor-pointer"
              @click="openStartOrderModal"
            >
              <div class="unstarted-icon-box bg-orange">
                <feather-icon icon="PlayCircleIcon" size="28" />
              </div>
              <div class="card-activity-title font-raleway font-weight-bold">Continuar</div>
              <div class="card-activity-timestamp">{{ statusStartedAtFormatted }}</div>
            </div>

            <!-- Inactive Card 2 (Iniciar when setup or other) -->
            <div
              v-else
              class="activity-card card-setup cursor-pointer"
              @click="openStartOrderModal"
            >
              <div class="unstarted-icon-box bg-orange">
                <feather-icon icon="PlayCircleIcon" size="28" />
              </div>
              <div class="card-activity-title font-raleway font-weight-bold">Iniciar</div>
              <div class="card-activity-timestamp">{{ statusStartedAtFormatted }}</div>
            </div>

            <!-- Card 3: Parar -->
            <div
              v-if="isUnstarted || currentStatus === 'unstarted'"
              class="activity-card card-unstarted cursor-pointer"
              @click="handleCardClick('parar')"
            >
              <div class="unstarted-icon-box bg-red">
                <feather-icon icon="PauseCircleIcon" size="32" />
              </div>
              <div class="card-activity-title font-raleway font-weight-bold">Parar</div>
            </div>

            <!-- Active Red Parar Card -->
            <div
              v-else-if="currentStatus === 'parar'"
              class="activity-card card-active-red cursor-pointer"
              @click="handleCardClick('parar')"
            >
              <div class="card-top-header">
                <div class="d-flex align-items-center">
                  <span class="radio-dot mr-50"></span>
                  <span class="card-state-label font-weight-bold font-raleway">{{ panelData && panelData.currentStatusName ? panelData.currentStatusName : 'Parar' }}</span>
                </div>
                <b-spinner small type="border" class="text-white opacity-8" />
              </div>

              <div class="card-timer-body text-center">
                <div class="timer-date-stamp">{{ statusStartedAtFormatted }}</div>
                <div class="timer-display">
                  {{ timerDisplay.hours }} : {{ timerDisplay.minutes }} : {{ timerDisplay.seconds }}
                </div>
                <div class="timer-labels">
                  <span>H</span>
                  <span>M</span>
                  <span>S</span>
                </div>
              </div>
            </div>

            <!-- Inactive Parar Card -->
            <div
              v-else
              class="activity-card card-parar cursor-pointer"
              @click="handleCardClick('parar')"
            >
              <div class="icon-circle icon-parar-bg">
                <feather-icon icon="SquareIcon" size="22" />
              </div>
              <div class="card-activity-title font-raleway font-weight-bold">Parar</div>
              <div class="card-activity-timestamp">{{ lastStopAtFormatted }}</div>
            </div>

            <!-- Card 4: Finalizar (Extrusão vs Ordem/Corte) -->
            <div
              v-if="isUnstarted || currentStatus === 'unstarted'"
              class="activity-card card-unstarted cursor-pointer"
              @click="handleCardClick('finalize')"
            >
              <div class="unstarted-icon-box bg-green">
                <feather-icon icon="CheckCircleIcon" size="32" />
              </div>
              <div class="card-activity-title font-raleway font-weight-bold">
                {{ isCuttingStep ? 'Finalizar ordem' : 'Finalizar extrusão' }}
              </div>
            </div>
            <div
              v-else
              class="activity-card card-finalizar cursor-pointer"
              @click="handleCardClick('finalize')"
            >
              <div class="icon-circle" :class="isCuttingStep ? 'icon-finalizar-cut-bg' : 'icon-finalizar-bg'">
                <feather-icon :icon="isCuttingStep ? 'CheckCircleIcon' : 'CheckIcon'" size="24" />
              </div>
              <div class="card-activity-title font-raleway font-weight-bold">
                {{ isCuttingStep ? 'Finalizar ordem' : 'Finalizar extrusão' }}
              </div>
              <div v-if="isCuttingStep && extrusionFinishedFormatted" class="card-activity-timestamp text-muted small">
                Extrusão: {{ extrusionFinishedFormatted }}
              </div>
            </div>
          </template>
        </div>

        <!-- ROW 5: Bottom Toolbar Row (Only shown when started) -->
        <div v-if="!isUnstarted" class="bottom-toolbar-grid">
          <button v-if="isCuttingStep && currentStatus !== 'parar' && currentStatus !== 'finished'" class="btn-toolbar-operator btn-outline-purple" @click="openCuttingBasketModal">
            <feather-icon icon="BoxIcon" size="16" class="mr-50" />
            <span>Apontamento do Corte</span>
          </button>

          <button class="btn-toolbar-operator btn-solid-brown" @click="handleOpenHistory">
            <feather-icon icon="ClockIcon" size="16" class="mr-50" />
            <span>Histórico</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Apontamento Parcial Tarugo (Fase 3) -->
    <TarugoAppointmentModal
      ref="tarugoModal"
      :order-code="orderCode"
      :preset-data="panelData"
      @submitted="fetchPanelData"
    />

    <!-- Modal Apontamento Geral de Extrusão e Corte (Start, Setup, Partial, Stop, Finish, Basket, CuttingRefuse, FinishCutting) -->
    <ExtrusionAppointmentModal
      :open.sync="showAppointmentModal"
      :action-type="modalActionType"
      :resource="startResourceObject"
      :site-id="siteId"
      :order-object="panelData"
      @confirm="handleAppointmentConfirmed"
    />

    <!-- Modal Histórico de Apontamentos Padrão SFC -->
    <ExtrusionHistoryModal
      v-if="showHistoryModal"
      :resource-id="resourceId"
      :resource-name="resourceTitle"
      :operation-id="activeOperationId"
      :order-code="orderCode"
      :site-id="siteId"
      :order-object="panelData"
      @close="showHistoryModal = false"
      @refresh="fetchPanelData"
    />

    <!-- Modal Detalhes da Ordem (Padrão Operador SFC) -->
    <OrderDetailsModal
      v-if="showModalOrder"
      :open.sync="showModalOrder"
      :order-data="orderDataForModal"
      @close="handleCloseOrderDetailsModal"
    />

    <!-- Modal Selecionar / Iniciar Nova OP da Prensa -->
    <SelectExtrusionOrderModal
      v-model="showSelectOrderModal"
      :resource="resourceObjectForOrderModal"
      :site-id="siteId"
      :before-select="confirmOrderSwitch"
      @confirmed="handleOrderConfirmed"
      @close="showSelectOrderModal = false"
    />
  </div>
</template>

<script>
import { BSpinner, BRow, BCol, BTooltip } from 'bootstrap-vue';
import { getExtrusionPanel, reopenExtrusionOrder } from './extrusionApi';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import AppointmentHeader from '@/views/pages/operator/components/AppointmentHeader';
import BackIcon from '@/assets/images/icons/back.svg';
import ShowDetailsIcon from '@core/assets/formatted-icons/show-details-icon.svg';
import AddCircleIcon from '@core/assets/icons/add-circle-md.svg';
import TarugoAppointmentModal from './components/TarugoAppointmentModal.vue';
import ExtrusionAppointmentModal from './components/ExtrusionAppointmentModal.vue';
import ExtrusionHistoryModal from './components/ExtrusionHistoryModal.vue';

export default {
  name: 'ExtrusionPanel',
  components: {
    AppointmentHeader,
    BSpinner,
    BRow,
    BCol,
    BTooltip,
    BackIcon,
    ShowDetailsIcon,
    AddCircleIcon,
    TarugoAppointmentModal,
    ExtrusionAppointmentModal,
    ExtrusionHistoryModal,
    OrderDetailsModal: () => import('@/views/pages/operator/components/OrderModal/OrderModal.vue'),
    SelectExtrusionOrderModal: () => import('./components/SelectExtrusionOrderModal.vue')
  },
  data() {
    return {
      panelData: null,
      loading: false,
      showAppointmentModal: false,
      showHistoryModal: false,
      showModalOrder: false,
      orderDataForModal: {},
      showSelectOrderModal: false,
      modalActionType: 'Start',
      statusStartedAtRaw: null,
      timerSeconds: 0,
      timerInterval: null,
      currentDateTimeFormatted: '',
      contextStatusList: [],
      isMobileScreen: false,
      isMetricsExpanded: false
    };
  },
  computed: {
    headerTitleText() {
      if (this.isUnstarted) return this.resourceTitle;
      const code = this.orderCode || this.activePanelData?.orderCode;
      return code ? `${this.resourceTitle} — OP #${code}` : this.resourceTitle;
    },
    statusBadgeColor() {
      return this.statusBadgeObject?.color || '#61b3fa';
    },
    statusBadgeObject() {
      if (this.isUnstarted || this.currentStatus === 'unstarted' || this.panelData?.currentStatusName === 'Não Iniciado' || this.panelData?.currentStatusId === 1) {
        const notStarted = this.contextStatusList.find(s => s.id === 1);
        if (notStarted) return notStarted;
        return { name: 'Não Iniciado', color: '#61b3fa' };
      }


      if (this.isCuttingStep && this.currentStatus !== 'finished' && this.currentStatus !== 'parar') {
        return { name: 'Corte', color: '#7367F0' };
      }

      const statusId = this.panelData?.currentStatusId || this.panelData?.statusId;
      if (statusId) {
        const found = this.contextStatusList.find(s => s.id === statusId);
        if (found) return found;
      }

      const currentName = this.panelData?.currentStatusName;
      if (currentName) {
        const foundByName = this.contextStatusList.find(s => (s.name || '').toLowerCase() === currentName.toLowerCase());
        if (foundByName) return foundByName;
        return { name: currentName, color: this.panelData?.currentStatusColor || this.panelData?.statusColor || '#37A964' };
      }

      if (this.isCuttingStep) {
        return { name: 'Corte', color: '#7367F0' };
      }

      return { name: 'Executando', color: '#37A964' };
    },
    statusBadgeText() {
      return this.statusBadgeObject.name || 'Executando';
    },
    statusBadgeStyle() {
      const rawColor = (this.statusBadgeObject.color || '#61b3fa').trim();
      let bg = `${rawColor}20`;
      if (rawColor.startsWith('#')) {
        let hex = rawColor.replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        if (hex.length >= 6) {
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            bg = `rgba(${r}, ${g}, ${b}, 0.15)`;
          }
        }
      }
      return {
        backgroundColor: bg,
        color: rawColor,
        borderRadius: '6px'
      };
    },
    orderCode() {
      return (this.panelData && (this.panelData.orderCode || this.panelData.activeOrderNumber)) || '';
    },
    activeOperationId() {
      return this.panelData?.operationId || (this.$route.query.operationId ? parseInt(this.$route.query.operationId, 10) : null) || (this.$route.params.operationId ? parseInt(this.$route.params.operationId, 10) : null);
    },
    isUnstarted() {
      return !this.panelData || !this.panelData.activeOrderId;
    },
    resourceTitle() {
      return (this.panelData && (this.panelData.resourceName || this.panelData.name)) || this.$route.query.resourceName || 'Prensa';
    },
    resourceId() {
      return this.$route.params.resourceId
        ? parseInt(this.$route.params.resourceId, 10)
        : (this.$route.query.resourceId ? parseInt(this.$route.query.resourceId, 10) : (this.panelData?.resourceId || null));
    },
    siteId() {
      const cookieSite = this.$cookies?.get('userInfo')?.currentSite;
      if (cookieSite) {
        if (typeof cookieSite === 'object' && cookieSite.id) return cookieSite.id;
        if (typeof cookieSite === 'number') return cookieSite;
        if (!isNaN(cookieSite)) return parseInt(cookieSite, 10);
      }
      const storedSite = localStorage.getItem('siteId') || localStorage.getItem('currentSite');
      if (storedSite) {
        try {
          const parsed = JSON.parse(storedSite);
          if (parsed && parsed.id) return parsed.id;
          if (typeof parsed === 'number') return parsed;
        } catch (e) {
          if (!isNaN(storedSite)) return parseInt(storedSite, 10);
        }
      }
      return 1;
    },
    resourceCode() {
      return (this.panelData && (this.panelData.resourceCode || this.panelData.code)) || this.$route.query.resourceCode || '';
    },
    startResourceObject() {
      return {
        resourceId: this.resourceId,
        name: this.resourceTitle,
        code: this.resourceCode
      };
    },
    resourceObjectForOrderModal() {
      return {
        resourceId: this.resourceId,
        id: this.resourceId,
        resourceName: this.resourceTitle,
        name: this.resourceTitle,
        resourceCode: this.resourceCode,
        code: this.resourceCode
      };
    },
    activePanelData() {
      if (this.panelData) return this.panelData;
      return {
        orderCode: '-',
        material: '-',
        matrix: '-',
        alloy: '-',
        temper: '-',
        extrusionSpeedTarget: 0,
        kghTarget: 0,
        efficiencyTarget: 0,
        totalKg: 0,
        barQuantity: 0,
        barSizeMm: 0,
        kghCalculated: 0,
        performancePercentage: 0,
        producedKg: 0,
        producedBars: 0,
        scrapKg: null,
        producedPercentage: 0,
        qualityPercentage: null,
        pressEfficiencyPercentage: 0,
        currentStep: 'Não Iniciado'
      };
    },
    siteId() {
      const cookieSite = this.$cookies?.get('userInfo')?.currentSite;
      if (cookieSite) {
        if (typeof cookieSite === 'object' && cookieSite.id) return cookieSite.id;
        if (typeof cookieSite === 'number') return cookieSite;
        if (!isNaN(cookieSite)) return parseInt(cookieSite, 10);
      }
      const storedSite = localStorage.getItem('siteId') || localStorage.getItem('currentSite');
      if (storedSite) {
        try {
          const parsed = JSON.parse(storedSite);
          if (parsed && parsed.id) return parsed.id;
          if (typeof parsed === 'number') return parsed;
        } catch (e) {
          if (!isNaN(storedSite)) return parseInt(storedSite, 10);
        }
      }
      return 1;
    },
    timerDisplay() {
      const h = Math.floor(this.timerSeconds / 3600);
      const m = Math.floor((this.timerSeconds % 3600) / 60);
      const s = this.timerSeconds % 60;

      const pad = num => String(num).padStart(2, '0');
      return {
        hours: pad(h),
        minutes: pad(m),
        seconds: pad(s)
      };
    },
    dynamicExtraCustomFields() {
      const customObj = this.activePanelData?.customParameters;
      if (!customObj || typeof customObj !== 'object') return [];

      const knownKeys = ['Alloy', 'Temper', 'Matrix', 'DieNumber', 'ExtrusionSpeedTarget', 'KghTarget', 'EfficiencyTarget', 'BarQuantity', 'BarSizeMm'];
      const result = [];

      Object.keys(customObj).forEach(key => {
        if (!knownKeys.includes(key)) {
          result.push({
            key,
            title: key,
            value: this.isUnstarted ? '-' : customObj[key],
            origin: 'Origem: Produto',
            tagClass: 'tag-product'
          });
        }
      });

      return result;
    },
    currentStatus() {
      const status = this.defineStatus();
      if (status === 'not-started') return 'unstarted';
      if (status === 'setup') return 'setup';
      if (status === 'started' || status === 'running') return 'parcial';
      if (status === 'finished') return 'finished';
      if (status === 'corte') return 'corte';
      if (status === 'stopped') return 'parar';
      return 'parcial';
    },
    statusStartedAtFormatted() {
      if (this.currentStatus === 'unstarted') return '-';
      const d = this.panelData?.statusStartedAt || this.panelData?.executionStartedAt;
      return d ? this.formatDateTime(d) : this.currentDateTimeFormatted;
    },
    lastStopAtFormatted() {
      const d = this.panelData?.lastStopAt;
      return d ? this.formatDateTime(d) : '-';
    },
    setupStartedAtFormatted() {
      const d = this.panelData?.setupStartedAt;
      return d ? this.formatDateTime(d) : '-';
    },
    isCuttingStep() {
      return this.panelData?.currentStep === 'Corte' || !!this.panelData?.extrusionFinishedAt;
    },
    extrusionFinishedFormatted() {
      const d = this.panelData?.extrusionFinishedAt;
      return d ? this.formatDateTime(d) : '';
    }
  },
  mounted() {
    this.startTimer();
    this.updateCurrentDateTime();
    this.fetchContextData();
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleWindowFocus);
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    window.removeEventListener('resize', this.checkScreenSize);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleWindowFocus);
  },
  watch: {
    resourceId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchPanelData();
        }
      }
    },
    '$route.query.operationId'() {
      this.fetchPanelData();
    },
    '$route.query.orderId'() {
      this.fetchPanelData();
    }
  },
  methods: {
    defineStatus() {
      if (this.isUnstarted) return 'not-started';

      const status = this.panelData?.currentStatusName;
      if (!status || status === 'Não Iniciado' || status === 'Not Started' || this.panelData?.currentStatusId === 1) {
        return 'not-started';
      }

      switch (status) {
        case 'Iniciado':
        case 'Started':
          return 'started';
        case 'Setup':
          return 'setup';
        case 'Executando':
        case 'Running':
          return this.isCuttingStep ? 'corte' : 'running';
        case 'Completed':
        case 'Operação Finalizada':
        case 'Concluído':
          return 'finished';
        case 'Corte':
          return 'corte';
        default:
          return 'stopped';
      }
    },
    checkScreenSize() {
      this.isMobileScreen = window.innerWidth < 992;
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      try {
        if (typeof dateStr === 'string' && dateStr.includes('T') && !dateStr.endsWith('Z')) {
          const parts = dateStr.split('T');
          const dateParts = parts[0].split('-');
          const timeParts = parts[1].split('.')[0].split(':');
          if (dateParts.length === 3 && timeParts.length >= 2) {
            const day = dateParts[2].padStart(2, '0');
            const month = dateParts[1].padStart(2, '0');
            const year = dateParts[0];
            const hh = timeParts[0].padStart(2, '0');
            const mm = timeParts[1].padStart(2, '0');
            const ss = (timeParts[2] || '00').padStart(2, '0');
            return `${day}.${month}.${year} ${hh}:${mm}:${ss}`;
          }
        }
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${day}.${month}.${year} ${hh}:${mm}:${ss}`;
      } catch (e) {
        return dateStr;
      }
    },
    async fetchContextData() {
      try {
        const response = await this.$http2.get(`/api/mysfc/operation-progress/context?siteId=${this.siteId}`);
        if (response.data && response.data.data) {
          this.contextStatusList = response.data.data.status || [];
        }
      } catch (e) {
        console.error('Erro ao carregar contexto no ExtrusionPanel:', e);
      }
    },
    async fetchPanelData() {
      const resId = this.resourceId;
      if (!resId) return;

      const opId = this.$route.query.operationId || this.$route.params.operationId || null;
      const ordId = this.$route.query.orderId || this.$route.params.orderId || null;

      this.loading = true;
      this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', true);
      try {
        const response = await getExtrusionPanel(resId, opId, ordId);
        const resData = response?.data;

        if (resData && typeof resData === 'object') {
          const raw = resData.data || resData;
          const prodParams = raw.productParameters || {};
          const orderParams = raw.orderParameters || {};

          const isFinishedOrder = raw.currentStatusId === 5 ||
            ['concluído', 'concluido', 'completed', 'operação finalizada', 'operacao finalizada', 'finalizado', 'finalizada'].includes((raw.currentStatusName || '').trim().toLowerCase()) ||
            raw.currentStep === 'Concluido';

          let startedAtStr = raw.firstEventTime || raw.setupStartedAt || raw.executionStartedAt;
          if (!startedAtStr && !isFinishedOrder) {
            startedAtStr = raw.statusStartedAt;
          }
          const finishedAtVal = raw.finishedAt || (isFinishedOrder ? (raw.statusStartedAt || raw.extrusionFinishedAt) : null);
          if (raw.currentStatusId === 2 && raw.setupStartedAt) {
            startedAtStr = raw.setupStartedAt;
          }

          this.statusStartedAtRaw = startedAtStr || null;
          this.updateTimer();

          const totalKgVal = Number(orderParams.total_kg) || 0;
          const barQtyVal = Number(orderParams.qtd_barras) || Number(raw.orderQuantity ?? raw.quantity ?? 0);
          let barSizeVal = Number(orderParams.tamanho_barra_mm) || Number(prodParams.tamanho_barra_mm) || 0;
          if (barSizeVal > 0 && barSizeVal <= 50) {
            barSizeVal *= 1000;
          }
          const matrixVal = orderParams.matriz || '-';

          const alloyVal = prodParams.liga || '-';
          const temperVal = prodParams.tempera || '-';
          const speedVal = Number(prodParams.velocidade_extrusao) || 0;
          const kghTargetVal = Number(prodParams.meta_kg_h) || 0;
          const effTargetVal = Number(prodParams.meta_eficiencia) || 0;

          this.panelData = {
            ...raw,
            startTime: raw.firstEventTime || startedAtStr,
            firstEventTime: raw.firstEventTime || startedAtStr,
            finishedAt: finishedAtVal,
            orderCode: raw.activeOrderNumber || '',
            material: raw.materialName || '',
            matrix: matrixVal,
            alloy: alloyVal,
            temper: temperVal,
            extrusionSpeedTarget: speedVal,
            kghTarget: kghTargetVal,
            efficiencyTarget: effTargetVal,
            totalKg: totalKgVal,
            barQuantity: barQtyVal,
            barSizeMm: barSizeVal,
            kghCalculated: raw.calculatedKgH || 0,
            performancePercentage: raw.performancePercentage || 0,
            producedKg: raw.totalProducedKg || 0,
            producedBars: raw.totalProducedBars || 0,
            scrapKg: (raw.totalCuttingRefuseBars !== undefined && raw.totalCuttingRefuseBars !== null && raw.totalCuttingRefuseBars > 0)
              ? raw.totalCuttingRefuseBars
              : ((raw.totalCuttingRefuseKg !== undefined && raw.totalCuttingRefuseKg !== null && raw.totalCuttingRefuseKg > 0)
                  ? raw.totalCuttingRefuseKg
                  : (raw.totalRefuseKg || 0)),
            totalCuttingRefuseKg: raw.totalCuttingRefuseKg || 0,
            totalCuttingRefuseBars: raw.totalCuttingRefuseBars || 0,
            totalBasketsCount: raw.totalBasketsCount || 0,
            totalBasketBars: raw.totalBasketBars || 0,
            extrusionFinishedAt: raw.extrusionFinishedAt || null,
            producedPercentage: (raw.producedPercentage !== undefined && raw.producedPercentage !== null)
              ? raw.producedPercentage
              : (raw.totalProducedBars && barQtyVal ? ((raw.totalProducedBars / barQtyVal) * 100) : 0),
            qualityPercentage: (raw.qualityPercentage !== undefined && raw.qualityPercentage !== null)
              ? raw.qualityPercentage
              : (raw.totalProducedBars > 0
                  ? Math.max(0, ((raw.totalProducedBars - (raw.totalCuttingRefuseBars || 0)) / raw.totalProducedBars) * 100)
                  : 100),
            pressEfficiencyPercentage: raw.pressEfficiencyPercentage ?? raw.oeePercentage ?? 0,
            currentStep: raw.currentStep || (raw.currentStatusName || (raw.activeOrderId ? 'Em Operação' : 'Não Iniciado')),
            resourceId: raw.resourceId || resId,
            resourceName: raw.resourceName || this.resourceTitle,
            operationId: raw.operationId,
            lastTarugoDetails: raw.lastTarugoDetails || null,
            availableAlloys: raw.availableAlloys || [],
            availableOrigins: raw.availableOrigins || []
          };
        } else {
          this.statusStartedAtRaw = null;
          this.updateTimer();
          this.panelData = this.buildEmptyPanelData(this.orderCode);
        }
      } catch (error) {
        console.error('Erro ao carregar dados do painel da extrusão:', error);
        this.statusStartedAtRaw = null;
        this.updateTimer();
        this.panelData = this.buildEmptyPanelData(this.orderCode);
      } finally {
        this.loading = false;
        this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', false);
      }
    },
    buildEmptyPanelData(code) {
      return {
        orderCode: code || '',
        material: '',
        matrix: '',
        alloy: '',
        temper: '',
        extrusionSpeedTarget: 0,
        kghTarget: 0,
        efficiencyTarget: 0,
        totalKg: 0,
        barQuantity: 0,
        barSizeMm: 0,
        kghCalculated: 0,
        performancePercentage: 0,
        producedKg: 0,
        producedBars: 0,
        scrapKg: null,
        availableAlloys: [],
        availableOrigins: [],
        producedPercentage: 0,
        qualityPercentage: null,
        pressEfficiencyPercentage: 0,
        currentStep: 'Em Operação'
      };
    },
    updateTimer() {
      if (this.currentStatus === 'unstarted') {
        this.timerSeconds = 0;
        return;
      }
      if (this.statusStartedAtRaw) {
        let start = new Date(this.statusStartedAtRaw).getTime();
        if (typeof this.statusStartedAtRaw === 'string' && this.statusStartedAtRaw.includes('T') && !this.statusStartedAtRaw.endsWith('Z')) {
          const parts = this.statusStartedAtRaw.split('T');
          const dateParts = parts[0].split('-').map(Number);
          const timeParts = parts[1].split('.')[0].split(':').map(Number);
          if (dateParts.length === 3 && timeParts.length >= 2) {
            start = new Date(dateParts[0], dateParts[1] - 1, dateParts[2], timeParts[0], timeParts[1], timeParts[2] || 0).getTime();
          }
        }
        const now = Date.now();
        if (!isNaN(start)) {
          this.timerSeconds = Math.max(0, Math.floor((now - start) / 1000));
          return;
        }
      }
      this.timerSeconds = 0;
    },
    startTimer() {
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.updateTimer();
      this.timerInterval = setInterval(() => {
        this.updateTimer();
      }, 1000);
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.updateTimer();
        this.updateCurrentDateTime();
      }
    },
    handleWindowFocus() {
      this.updateTimer();
      this.updateCurrentDateTime();
    },
    updateCurrentDateTime() {
      const now = new Date();
      const d = String(now.getDate()).padStart(2, '0');
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const y = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      this.currentDateTimeFormatted = `${d} ${m} ${y} ${hh}:${mm}`;
    },
    handleCardClick(cardType) {
      switch (cardType) {
        case 'setup':
          this.openAppointmentModal('Setup');
          break;
        case 'tarugo':
          if (this.isCuttingStep) {
            this.openCuttingBasketModal();
          } else {
            this.openTarugoModal();
          }
          break;
        case 'parar':
          this.openAppointmentModal('Stop');
          break;
        case 'finalize':
          if (this.isCuttingStep) {
            const prodBars = Number(this.panelData?.totalProducedBars || this.panelData?.producedBars || 0);
            const basketsCount = Number(this.panelData?.totalBasketsCount || 0);
            const cuttingRefuseBars = Number(this.panelData?.totalCuttingRefuseBars || 0);
            const cuttingRefuseKg = Number(this.panelData?.totalCuttingRefuseKg || 0);

            if (prodBars <= 0) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Finalização Bloqueada',
                  icon: 'AlertCircleIcon',
                  text: 'Não é possível finalizar o corte sem produção de extrusão apontada.',
                  variant: 'danger'
                }
              });
              return;
            }

            if (basketsCount <= 0 && cuttingRefuseBars <= 0 && cuttingRefuseKg <= 0) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Finalização Bloqueada',
                  icon: 'AlertCircleIcon',
                  text: 'Não é possível finalizar o corte sem realizar apontamentos de quantidade (cestos ou refugos).',
                  variant: 'danger'
                }
              });
              return;
            }

            this.openCuttingFinalizeModal();
          } else {
            const prodBars = Number(this.panelData?.totalProducedBars || this.panelData?.producedBars || 0);
            const prodKg = Number(this.panelData?.totalProducedKg || this.panelData?.producedKg || 0);

            if (this.isUnstarted || this.currentStatus === 'unstarted') {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Finalização Bloqueada',
                  icon: 'AlertCircleIcon',
                  text: 'A ordem ainda não foi iniciada. Inicie a ordem e aponte quantidades antes de finalizar.',
                  variant: 'danger'
                }
              });
              return;
            }

            if (prodBars <= 0 && prodKg <= 0) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Finalização Bloqueada',
                  icon: 'AlertCircleIcon',
                  text: 'Não é possível finalizar a extrusão sem realizar apontamentos de quantidade (barras produzidas).',
                  variant: 'danger'
                }
              });
              return;
            }

            this.openAppointmentModal('Finish');
          }
          break;
      }
    },
    openCuttingRefuseModal() {
      this.openAppointmentModal('CuttingRefuse');
    },
    openCuttingBasketModal() {
      this.openAppointmentModal('Basket');
    },
    openCuttingFinalizeModal() {
      this.openAppointmentModal('FinishCutting');
    },
    openTarugoModal() {
      this.openAppointmentModal('Partial');
    },
    openAppointmentModal(type = 'Start') {
      this.modalActionType = type;
      this.showAppointmentModal = true;
    },
    openStartOrderModal() {
      this.openAppointmentModal('Start');
    },
    handleAppointmentConfirmed() {
      this.showAppointmentModal = false;
      this.fetchPanelData();
    },
    handleOrderSelected() {
      this.showAppointmentModal = false;
      this.fetchPanelData();
    },
    handleOpenHistory() {
      this.showHistoryModal = true;
    },
    async promptReopenOrder() {
      const res = await this.$swal({
        title: 'Reabrir Ordem?',
        text: 'Deseja realmente reabrir esta ordem de produção? A finalização será estornada e você poderá ajustar ou realizar novos apontamentos.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, Reabrir Ordem',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1'
        },
        buttonsStyling: false
      });

      if (res && (res.isConfirmed || res.value === true)) {
        try {
          this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', true);
          const opId = this.panelData?.operationId || this.activeOperationId;
          const resId = this.resourceId || this.panelData?.resourceId;

          await reopenExtrusionOrder({
            operationId: opId,
            resourceId: resId
          });

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Ordem Reaberta',
              icon: 'CheckIcon',
              text: 'A ordem foi reaberta com sucesso! Você pode continuar os apontamentos.',
              variant: 'success'
            }
          });

          await this.fetchPanelData();
        } catch (err) {
          console.error('Erro ao reabrir ordem:', err);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Erro',
              icon: 'AlertCircleIcon',
              text: err.response?.data?.message || 'Não foi possível reabrir a ordem.',
              variant: 'danger'
            }
          });
        } finally {
          this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', false);
        }
      }
    },
    openSelectNewOrderModal() {
      this.showSelectOrderModal = true;
    },
    async confirmOrderSwitch(newOrder) {
      if (!newOrder) return false;

      const currentOrderId = this.panelData?.orderId || this.$route.query.orderId;
      const currentOperationId = this.panelData?.operationId || this.activeOperationId;

      if ((newOrder.orderId && String(newOrder.orderId) === String(currentOrderId)) ||
          (newOrder.operationId && String(newOrder.operationId) === String(currentOperationId))) {
        this.showSelectOrderModal = false;
        return false;
      }

      const isCurrentActive = !this.isUnstarted && (
        this.currentStatus === 'running' ||
        this.currentStatus === 'setup' ||
        this.currentStatus === 'started' ||
        this.currentStatus === 'corte'
      );

      if (isCurrentActive) {
        const currentCode = this.orderCode || this.activePanelData?.orderCode || currentOrderId || '';
        const newCode = newOrder.orderCode || newOrder.code || newOrder.orderNumber || newOrder.orderId || '';

        const res = await this.$swal({
          title: 'Alternar Ordem de Produção?',
          text: `A OP atual (#${currentCode}) ainda está em andamento. Deseja alternar para a OP #${newCode} agora?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, Alternar OP',
          cancelButtonText: 'Cancelar',
          customClass: {
            container: 'extrusion-order-switch-confirmation',
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1'
          },
          buttonsStyling: false
        });

        const confirmed = res && (res.isConfirmed || res.value === true);
        return !!confirmed;
      }

      return true;
    },
    handleOrderConfirmed({ selectedOrder, orderId, operationId }) {
      this.showSelectOrderModal = false;
      this.$nextTick(() => {
        this.fetchPanelData();
      });
    },
    buildFallbackOrderData() {
      const p = this.panelData || {};
      const a = this.activePanelData || {};
      const planned = Number(a.barQuantity || p.barQuantity || p.orderQuantity || p.plannedQuantity || 0);
      const produced = Number(a.producedBars || p.producedBars || p.totalProducedBars || p.producedQuantity || 0);
      const refuse = Number(a.scrapKg || p.scrapKg || p.totalRefuseKg || p.refuseQuantity || 0);
      const realProg = (a.producedPercentage !== undefined && a.producedPercentage !== null)
        ? (a.producedPercentage / 100)
        : (planned > 0 ? (produced / planned) : 0);
      const theoProg = a.efficiencyTarget ? (a.efficiencyTarget / 100) : (p.performancePercentage ? (p.performancePercentage / 100) : 0);

      return {
        id: p.operationId || this.activeOperationId || 0,
        orderId: p.activeOrderId || p.orderId || (this.$route.query.orderId ? parseInt(this.$route.query.orderId, 10) : 0),
        orderNumber: a.orderCode || p.orderCode || p.activeOrderNumber || '-',
        operationNumber: p.operationNumber || p.opNumber || '1',
        operationName: p.operationName || 'Extrusão',
        product: a.material || p.material || p.materialName || '-',
        productCode: p.productCode || a.material || '-',
        resources: this.resourceTitle || a.resourceName || '-',
        workcenter: p.workcenter || 'Extrusão',
        plannedQuantity: planned,
        producedQuantity: produced,
        reworkQuantity: 0,
        refuseQuantity: refuse,
        priority: p.priority || 1,
        dueDate: p.dueDate || '-',
        setupStart: p.setupStart || '-',
        startTime: p.startTime || '-',
        endTime: p.endTime || '-',
        attributes: p.attributes || [],
        note: p.note || p.observation || '',
        theoricalProgress: theoProg,
        realProgress: realProg,
        statusData: []
      };
    },
    async openOrderDetailsModal() {
      if (this.isUnstarted && !this.orderCode) {
        if (this.$bvToast) {
          this.$bvToast.toast('Nenhuma ordem de produção selecionada no momento.', {
            title: 'Aviso',
            variant: 'warning',
            solid: true
          });
        }
        return;
      }

      const orderId = this.panelData?.activeOrderId || this.panelData?.orderId || (this.$route.query.orderId ? parseInt(this.$route.query.orderId, 10) : null);
      const opId = this.panelData?.operationId || this.activeOperationId || null;
      const currentResourceId = this.resourceId;
      const fallback = this.buildFallbackOrderData();
      this.orderDataForModal = { ...fallback };

      if (orderId || opId) {
        try {
          this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', true);
          const url = `/api/mysfc/operator/operator-order?SiteId=${this.siteId}&orderId=${orderId || ''}`;
          const response = await this.$http2.get(url);
          let data = response?.data?.data;
          if (Array.isArray(data)) {
            // Prioritize matching operation for this extrusion press:
            // 1. By exact operation ID of the extrusion
            // 2. By machine/resource ID associated with the operation
            // 3. By operation name containing "extrus"
            let found = null;
            if (opId) {
              found = data.find(item => item.id == opId);
            }
            if (!found && currentResourceId) {
              found = data.find(item => item.resources?.some(r => r.id == currentResourceId));
            }
            if (!found) {
              found = data.find(item => item.operationName && item.operationName.toLowerCase().includes('extrus'));
            }
            data = found || data[0];
          }

          if (data && typeof data === 'object') {
            // Merge with fallback, ensuring numbers and resources are sanitized to prevent NaN and wrong data
            this.orderDataForModal = {
              ...fallback,
              ...data,
              // Keep non-null values for critical identity fields
              orderNumber: data.orderNumber || fallback.orderNumber,
              operationNumber: data.operationNumber != null ? data.operationNumber : fallback.operationNumber,
              operationName: data.operationName || fallback.operationName,
              product: data.product || fallback.product,
              productCode: data.productCode || fallback.productCode,
              resources: (data.resources && (Array.isArray(data.resources) ? data.resources.length > 0 : true))
                ? data.resources
                : fallback.resources,
              workcenter: data.workcenter || fallback.workcenter,
              // Numeric protection: never allow null/undefined/NaN into OrderModal
              plannedQuantity: (data.plannedQuantity != null && !isNaN(data.plannedQuantity) && Number(data.plannedQuantity) > 0)
                ? Number(data.plannedQuantity)
                : fallback.plannedQuantity,
              producedQuantity: (data.producedQuantity != null && !isNaN(data.producedQuantity) && Number(data.producedQuantity) > 0)
                ? Number(data.producedQuantity)
                : fallback.producedQuantity,
              reworkQuantity: (data.reworkQuantity != null && !isNaN(data.reworkQuantity))
                ? Number(data.reworkQuantity)
                : 0,
              refuseQuantity: (data.refuseQuantity != null && !isNaN(data.refuseQuantity) && Number(data.refuseQuantity) > 0)
                ? Number(data.refuseQuantity)
                : fallback.refuseQuantity,
              theoricalProgress: (data.theoricalProgress != null && !isNaN(data.theoricalProgress) && Number(data.theoricalProgress) > 0)
                ? Number(data.theoricalProgress)
                : fallback.theoricalProgress,
              realProgress: (data.realProgress != null && !isNaN(data.realProgress) && Number(data.realProgress) > 0)
                ? Number(data.realProgress)
                : fallback.realProgress
            };
          }
        } catch (err) {
          console.warn('Não foi possível carregar dados completos da ordem via API, utilizando fallback local:', err);
        } finally {
          this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', false);
        }
      }

      this.showModalOrder = true;
    },
    async handleCloseOrderDetailsModal() {
      this.showModalOrder = false;
      try {
        await this.fetchPanelData();
      } catch (err) {
        console.error('Erro ao recarregar painel ao fechar modal de detalhes:', err);
      } finally {
        this.$store?.commit('app/UPDATE_FLAG_SHOW_OVERLAY', false);
      }
    },
    goBack() {
      const prefix = this.$route.params.prefix || 'sfc';
      this.$router.push({
        name: 'operator-extrusion-resources',
        params: { prefix }
      });
    },
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return '0';
      return Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
    },
    formatInt(val) {
      if (val === null || val === undefined || isNaN(val)) return '0';
      return Number(val).toLocaleString('pt-BR');
    },
    formatPercent(val) {
      if (val === null || val === undefined || isNaN(val)) return '0%';
      const num = val > 1 ? val : val * 100;
      return `${num.toFixed(0)}%`;
    },
    formatPercentValue(val) {
      if (val === null || val === undefined || isNaN(val)) return '0%';
      return `${Number(val).toFixed(0)}%`;
    }
  }
};
</script>

<style lang="scss">
.app-content-operator:has(#extrusion-panel-container) {
  padding: 0 !important;
}

body:has(#extrusion-panel-container) .footer,
body:has(#extrusion-panel-container) footer {
  display: none !important;
}
</style>

<style lang="scss" scoped>
#extrusion-panel-container {
  width: 100%;
  min-height: calc(100vh - 110px);
  padding-top: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  color: #4c4541;
  background: #f8f9fa;
  box-sizing: border-box;

  /* Fixed Top Header Bar */
  .header-bar {
    background: #fff;
    border-top: 1px solid #cfc4be;
    border-bottom: 1px solid #cfc4be;
    padding: 0 16px;
    height: 48px;
    min-height: 48px;
    display: flex;
    flex-direction: row !important;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 1000;

    .header-left {
      display: flex;
      align-items: center;
      min-width: 0;
      flex: 1;
      margin-right: 12px;

      .header__back {
        display: flex;
        align-items: center;
        cursor: pointer;
        margin-right: 10px;
        flex-shrink: 0;
        transition: transform 0.2s;

        &:hover {
          transform: translateX(-3px);
        }
      }

      .header__title {
        font-size: 16px;
        line-height: 20px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }
  }

  .btn-operator-refresh {
    height: 32px;
    padding: 0 12px;
    background: #fff;
    border: 1px solid #cfc4be;
    border-radius: 6px;
    color: #4c4541;
    font-weight: 600;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: #974900;
      color: #974900;
      background: #ffede2;
    }
  }

  .spin-icon {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    100% { transform: rotate(360deg); }
  }

  /* Main Operator Body */
  .operator-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    box-sizing: border-box;
  }

  .loading-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #cfc4be;
    padding: 40px;
  }

  .panel-layout-grid {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 12px;
  }

  /* Color Tags for Origins */
  .tag-op { color: #0056b3 !important; }
  .tag-product { color: #6f42c1 !important; }
  .tag-extrusion { color: #974900 !important; }
  .tag-cut { color: #dc3545 !important; }
  .tag-calc { color: #d39e00 !important; }

  /* ROW 1: Header Specs Grid (CSS Grid: 8 items) */
  .specs-grid-container {
    display: grid;
    grid-template-columns: minmax(75px, 1fr) minmax(140px, 2fr) repeat(6, minmax(75px, 1fr));
    gap: 6px;
    width: 100%;
    flex-shrink: 0;

    .spec-card {
      background: #fff;
      border: 1px solid #cfc4be;
      border-radius: 6px;
      padding: 5px 8px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      overflow: hidden;

      .spec-card-title {
        font-family: "Raleway", sans-serif;
        font-size: 10px;
        color: #7e7570;
        line-height: 12px;
        white-space: nowrap;
      }

      .spec-card-value {
        font-family: "Raleway", sans-serif;
        font-size: 13px;
        color: #4c4541;
        line-height: 16px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      .spec-card-origin {
        font-size: 8.5px;
        font-weight: 700;
        line-height: 10px;
        white-space: nowrap;
        margin-top: 1px;
      }
    }
  }

  /* ROW 2: Data Origin Legend Sub-Bar */
  .legend-bar {
    font-family: "Raleway", sans-serif;
    font-size: 10.5px;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 6px;
    color: #4c4541;
    flex-shrink: 0;
    line-height: 14px;
    overflow: hidden;

    .legend-label {
      font-weight: 700;
      color: #7e7570;
      white-space: nowrap;
    }

    .legend-item {
      white-space: nowrap;
    }

    .legend-sep {
      color: #cfc4be;
    }
  }

  /* ROW 3: 11 KPI Metrics Grid (CSS Grid: Exactly 11 equal columns) */
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(11, minmax(0, 1fr));
    gap: 6px;
    width: 100%;
    flex-shrink: 0;

    .metric-card {
      background: #fff;
      border: 1px solid #cfc4be;
      border-radius: 6px;
      padding: 5px 4px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      .metric-title {
        font-family: "Raleway", sans-serif;
        font-size: 10px;
        color: #7e7570;
        white-space: nowrap;
        line-height: 12px;
      }

      .metric-value {
        font-family: "Raleway", sans-serif;
        font-size: 14px;
        font-weight: 700;
        color: #4c4541;
        line-height: 18px;
        white-space: nowrap;

        &.text-orange-highlight {
          color: #e67e22 !important;
        }
      }

      .metric-origin {
        font-size: 8.5px;
        font-weight: 700;
        white-space: nowrap;
        line-height: 10px;
      }
    }
  }

  /* ROW 4: Operator Activity Cards */
  .activity-cards-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    flex: 1;
    align-items: stretch;

    &.unstarted-cards-grid,
    &.cutting-cards-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }

    .activity-card {
      background: #fff;
      border: 1px solid #cfc4be;
      border-radius: 10px;
      height: 100%;
      min-height: 220px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      user-select: none;
      position: relative;
      overflow: hidden;

      &:hover {
        border-color: #974900;
        box-shadow: 0 4px 14px rgba(151, 73, 0, 0.1);
      }

      &.card-unstarted {
        background: #fdfdfd;

        .unstarted-icon-box {
          width: 58px;
          height: 58px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          margin-bottom: 14px;

          &.bg-slate {
            background-color: #607d8b;
          }
          &.bg-orange {
            background-color: #ff9f43;
          }
          &.bg-red {
            background-color: #ea5455;
          }
          &.bg-green {
            background-color: #28c76f;
          }
          &.bg-purple {
            background-color: #7367f0;
          }
        }
      }

      &.card-setup {
        background: #faf6f0;

        .icon-setup-bg {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          border: 1px solid #d4c5b9;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #974900;
          margin-bottom: 12px;
        }
      }

      &.card-active-slate {
        background: #607d8b !important;
        border-color: #455a64 !important;
        color: #fff !important;
        justify-content: space-between;

        .card-top-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px 0 12px;

          .card-state-label {
            font-size: 14px;
            color: #fff;
          }

          .radio-dot {
            width: 9px;
            height: 9px;
            background: #fff;
            border-radius: 50%;
            display: inline-block;
          }
        }

        .card-timer-body {
          padding: 8px 12px;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: auto 0;

          .timer-date-stamp {
            font-size: 11px;
            opacity: 0.9;
            font-family: "Raleway", sans-serif;
            margin-bottom: 2px;
          }

          .timer-display {
            font-size: 34px;
            line-height: 38px;
            letter-spacing: 2px;
            font-family: monospace, sans-serif;
            font-weight: 700;
          }

          .timer-labels {
            display: flex;
            justify-content: center;
            font-size: 10px;
            opacity: 0.85;
            gap: 28px;
            margin-top: 1px;
          }
        }
      }

      &.card-active-red {
        background: #ea5455 !important;
        border-color: #d64546 !important;
        color: #fff !important;

        .card-top-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px 0 12px;

          .card-state-label {
            font-size: 14px;
            color: #fff;
          }

          .radio-dot {
            width: 9px;
            height: 9px;
            background: #fff;
            border-radius: 50%;
            display: inline-block;
          }
        }

        .card-timer-body {
          padding: 8px 12px;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: auto 0;

          .timer-date-stamp {
            font-size: 11px;
            opacity: 0.9;
            font-family: "Raleway", sans-serif;
            margin-bottom: 2px;
          }

          .timer-display {
            font-size: 34px;
            line-height: 38px;
            letter-spacing: 2px;
            font-family: monospace, sans-serif;
            font-weight: 700;
          }

          .timer-labels {
            display: flex;
            justify-content: center;
            font-size: 10px;
            opacity: 0.85;
            gap: 28px;
            margin-top: 1px;
          }
        }
      }

      &.card-active-orange {
        background: #e68a00 !important;
        border-color: #d47e00 !important;
        color: #fff !important;

        .card-top-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px 0 12px;

          .card-state-label {
            font-size: 14px;
            color: #fff;
          }

          .radio-dot {
            width: 9px;
            height: 9px;
            background: #fff;
            border-radius: 50%;
            display: inline-block;
          }
        }

        .card-timer-body {
          padding: 8px 12px;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: auto 0;

          .timer-date-stamp {
            font-size: 11px;
            opacity: 0.9;
            font-family: "Raleway", sans-serif;
            margin-bottom: 2px;
          }

          .timer-display {
            font-size: 34px;
            line-height: 38px;
            letter-spacing: 2px;
            font-family: monospace, sans-serif;
            font-weight: 700;
          }

          .timer-labels {
            display: flex;
            justify-content: center;
            font-size: 10px;
            opacity: 0.85;
            gap: 28px;
            margin-top: 1px;
          }
        }

        .card-bottom-button {
          width: 100%;
          padding: 8px 0;
          background: rgba(0, 0, 0, 0.15);
          color: #fff;
          font-weight: 700;
          font-size: 13px;
          font-family: "Raleway", sans-serif;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          text-align: center;

          &:hover {
            background: rgba(0, 0, 0, 0.25);
          }
        }
      }

      &.card-active-purple {
        background: #7367f0 !important;
        border-color: #5e50ee !important;
        color: #fff !important;

        .card-top-header {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px 0 12px;

          .card-state-label {
            font-size: 14px;
            color: #fff;
          }

          .radio-dot {
            width: 9px;
            height: 9px;
            background: #fff;
            border-radius: 50%;
            display: inline-block;
          }
        }

        .card-timer-body {
          padding: 8px 12px;
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: auto 0;

          .timer-date-stamp {
            font-size: 11px;
            opacity: 0.9;
            font-family: "Raleway", sans-serif;
            margin-bottom: 2px;
          }

          .timer-display {
            font-size: 34px;
            line-height: 38px;
            letter-spacing: 2px;
            font-family: monospace, sans-serif;
            font-weight: 700;
          }

          .timer-labels {
            display: flex;
            justify-content: center;
            font-size: 10px;
            opacity: 0.85;
            gap: 28px;
            margin-top: 1px;
          }
        }

        .card-bottom-button {
          width: 100%;
          padding: 8px 0;
          background: rgba(0, 0, 0, 0.15);
          color: #fff;
          font-weight: 700;
          font-size: 13px;
          font-family: "Raleway", sans-serif;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          text-align: center;

          &:hover {
            background: rgba(0, 0, 0, 0.25);
          }
        }
      }

      &.card-active-green {
        background: #28c76f !important;
        border-color: #20a359 !important;
        color: #fff !important;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 20px;
        box-shadow: 0 4px 16px rgba(40, 199, 111, 0.25);

        .icon-square-white {
          width: 56px;
          height: 56px;
          background: #fff;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .card-activity-title {
          color: #fff !important;
        }
      }

      &.card-parar {
        .icon-parar-bg {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #ff4d4f;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          margin-bottom: 12px;
          box-shadow: 0 4px 12px rgba(255, 77, 79, 0.25);
        }

        .card-activity-timestamp {
          font-size: 11px;
          color: #7e7570;
          margin-top: 4px;
        }
      }

      &.card-finalizar {
        .icon-finalizar-bg {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #28a745;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          margin-bottom: 12px;
          box-shadow: 0 4px 12px rgba(40, 167, 69, 0.25);
        }

        .icon-finalizar-cut-bg {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #7367f0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          margin-bottom: 12px;
          box-shadow: 0 4px 12px rgba(115, 103, 240, 0.25);
        }
      }

      .card-activity-title {
        font-size: 16px;
        color: #4c4541;
      }
    }
  }

  /* ROW 5: Bottom Toolbar */
  .bottom-toolbar-grid {
    display: flex;
    gap: 10px;
    width: 100%;
    flex-shrink: 0;

    .btn-toolbar-operator {
      flex: 1;
      height: 44px;
      border-radius: 8px;
      font-family: "Raleway", sans-serif;
      font-size: 14px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;

      &.btn-outline-brown {
        background: #fff;
        border: 1px solid #974900;
        color: #974900;

        &:hover {
          background: #ffede2;
        }
      }

      &.btn-solid-brown {
        background: #974900;
        border: 1px solid #974900;
        color: #fff;

        &:hover {
          background: #9f5714;
          border-color: #9f5714;
        }

        &:active {
          background: #a45f1f;
          border-color: #a45f1f;
        }
      }
    }
  }

  .metrics-accordion-header {
    display: none;
    background: #fff;
    border: 1px solid #cfc4be;
    border-radius: 8px;
    padding: 10px 14px;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);

    &:hover {
      background: #ffede2;
      border-color: #974900;
    }
  }

  .passive-metrics-wrapper {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  /* ==========================================================================
     RESPONSIVE MEDIA QUERIES (DESKTOP, LAPTOP, TABLET & MOBILE)
     ========================================================================== */

  /* 1. Large Laptops / Medium Desktops (max-width: 1400px) */
  @media (max-width: 1400px) {
    .metrics-grid {
      grid-template-columns: repeat(6, minmax(0, 1fr));
      gap: 6px;
    }
  }

  /* 2. Standard Tablets Landscape / Laptops (max-width: 991px) */
  @media (max-width: 991px) {
    .metrics-accordion-header {
      display: flex !important;
    }

    .panel-layout-grid {
      display: flex !important;
      flex-direction: column !important;
      gap: 12px;
    }

    .metrics-accordion-header {
      order: 1 !important;
    }

    .passive-metrics-wrapper {
      order: 2 !important;
    }

    .activity-cards-grid {
      order: 3 !important;
    }

    .bottom-toolbar-grid {
      order: 4 !important;
    }

    .header-bar {
      height: 48px;
      padding: 0 12px;

      .header-left .header__title {
        font-size: 14.5px;
      }
    }

    .specs-grid-container {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 6px;

      .spec-actions-container {
        grid-column: span 4;
        justify-content: flex-end;
        margin-top: 2px;
      }
    }

    .metrics-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 6px;
    }

    .activity-cards-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;

      &.unstarted-cards-grid,
      &.cutting-cards-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }

      .activity-card {
        min-height: 180px;

        .card-timer-body {
          .timer-display {
            font-size: 28px;
            line-height: 32px;
          }
        }
      }
    }
  }

  /* 3. Tablets Portrait / Mobile Large (max-width: 768px) */
  @media (max-width: 768px) {
    min-height: 100vh;
    height: auto !important;
    overflow-y: auto !important;
    padding-top: 49px;

    .header-bar {
      height: 44px;
      min-height: 44px;
      padding: 0 10px;

      .header-left .header__title {
        font-size: 13.5px;
      }
    }

    .operator-body {
      padding: 10px;
      gap: 10px;
    }

    .specs-grid-container {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px;

      .spec-actions-container {
        grid-column: span 2;
        justify-content: flex-end;
      }
    }

    .legend-bar {
      flex-wrap: wrap;
      font-size: 9.5px;
      line-height: 14px;
      gap: 4px 8px;
    }

    .metrics-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 6px;

      .metric-card {
        padding: 5px 2px;

        .metric-title {
          font-size: 9.5px;
        }

        .metric-value {
          font-size: 13px;
        }
      }
    }

    .activity-cards-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;

      &.unstarted-cards-grid {
        grid-template-columns: 1fr !important;
      }

      .activity-card {
        min-height: 160px;

        .card-timer-body {
          .timer-display {
            font-size: 24px;
            line-height: 28px;
            letter-spacing: 1px;
          }

          .timer-labels {
            gap: 20px;
          }
        }
      }
    }

    .bottom-toolbar-grid {
      flex-direction: column;
      gap: 8px;

      .btn-toolbar-operator {
        height: 42px;
        font-size: 13.5px;
      }
    }
  }

  /* 4. Mobile Small (max-width: 576px) */
  @media (max-width: 576px) {
    .operator-body {
      padding: 8px;
      gap: 8px;
    }

    .specs-grid-container {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 4px;

      .spec-card {
        padding: 4px;

        .spec-card-title {
          font-size: 9px;
        }

        .spec-card-value {
          font-size: 11.5px;
        }
      }

      .spec-actions-container {
        grid-column: span 2;
        justify-content: space-between;

        .spec-action-btn {
          flex: 1;
        }
      }
    }

    .metrics-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 4px;

      .metric-card {
        padding: 4px 2px;

        .metric-title {
          font-size: 9px;
        }

        .metric-value {
          font-size: 12px;
        }
      }
    }

    .activity-cards-grid {
      grid-template-columns: 1fr;
      gap: 8px;

      .activity-card {
        min-height: 140px;

        .card-timer-body {
          .timer-display {
            font-size: 24px;
            line-height: 28px;
          }
        }
      }
    }

    .bottom-toolbar-grid {
      flex-direction: column;
      gap: 6px;

      .btn-toolbar-operator {
        height: 38px;
        font-size: 13px;
      }
    }
  }

  /* Cutting Stage Styles */
  .cutting-banner {
    background: linear-gradient(135deg, #f4f2ff 0%, #ede9fe 100%);
    border: 1px solid #c4b5fd;
    border-radius: 8px;
    padding: 10px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 6px rgba(115, 103, 240, 0.08);

    .cutting-banner-left {
      display: flex;
      align-items: center;
      gap: 12px;

      .cutting-pulse-dot {
        width: 12px;
        height: 12px;
        background: #7367f0;
        border-radius: 50%;
        box-shadow: 0 0 0 0 rgba(115, 103, 240, 0.7);
        animation: pulse-purple 1.8s infinite;
        flex-shrink: 0;
      }

      .cutting-banner-title {
        font-size: 13.5px;
        color: #5e50ee;
        line-height: 16px;
      }

      .cutting-banner-subtitle {
        font-size: 11px;
        color: #6e6b7b;
        line-height: 14px;
        margin-top: 2px;
      }
    }

    .cutting-banner-right {
      .btn-banner-refuse {
        background: #fff;
        border: 1px solid #ea5455;
        color: #ea5455;
        padding: 6px 14px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 700;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: #ea5455;
          color: #fff;
        }
      }
    }
  }

  @keyframes pulse-purple {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(115, 103, 240, 0.7);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 8px rgba(115, 103, 240, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(115, 103, 240, 0);
    }
  }

  .card-active-purple {
    background: #7367f0 !important;
    border-color: #5e50ee !important;
    color: #fff !important;

    .card-top-header {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px 0 12px;

      .card-state-label {
        font-size: 14px;
        color: #fff;
      }

      .radio-dot {
        width: 9px;
        height: 9px;
        background: #fff;
        border-radius: 50%;
        display: inline-block;
      }
    }

    .card-timer-body {
      padding: 8px 12px;
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin: auto 0;

      .timer-date-stamp {
        font-size: 11px;
        opacity: 0.9;
        font-family: "Raleway", sans-serif;
        margin-bottom: 2px;
      }

      .timer-display {
        font-size: 34px;
        line-height: 38px;
        letter-spacing: 2px;
        font-family: monospace, sans-serif;
        font-weight: 700;
      }

      .timer-labels {
        display: flex;
        justify-content: center;
        font-size: 10px;
        opacity: 0.85;
        gap: 28px;
        margin-top: 1px;
      }
    }

    .card-bottom-button {
      background: rgba(255, 255, 255, 0.2);
      width: 100%;
      padding: 6px;
      text-align: center;
      font-size: 12px;
      font-weight: 700;
      border-top: 1px solid rgba(255, 255, 255, 0.25);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
      }
    }
  }

  .icon-finalizar-cut-bg {
    background: #eef2ff;
    color: #7367f0;
  }

  .btn-outline-purple {
    background: #fff;
    border: 1.5px solid #7367f0;
    color: #7367f0;

    &:hover {
      background: #7367f0;
      color: #fff;
    }
  }

  .extrusion-header-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 0 16px;
    align-self: stretch;
    flex-shrink: 0;
    border-right: 1px solid #cfc4be;
    background-color: #fff;

    .btn-extrusion-header-action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 38px;
      height: 38px;
      min-width: 38px;
      min-height: 38px;
      border-radius: 50%;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 0;
      margin: 0;
      outline: none;
      transition: all 0.2s ease-in-out;

      &:hover:not(:disabled) {
        background-color: #ffede2;
        transform: scale(1.08);
      }

      &:active:not(:disabled) {
        background-color: #ffdbc4;
        transform: scale(0.95);
      }

      &:disabled,
      &.btn-disabled {
        opacity: 0.35;
        cursor: not-allowed;
        pointer-events: none;
      }

      .header-action-svg,
      svg {
        width: 22px !important;
        height: 22px !important;
        display: block;
        fill: #974900;
        path {
          fill: #974900;
        }

        &.icon-order-details {
          width: 22px !important;
          height: 23px !important;
        }
      }

      &#btn-extrusion-order-details svg {
        width: 22px !important;
        height: 23px !important;
      }
    }
  }
}
</style>

<style lang="scss">
.swal2-container.extrusion-order-switch-confirmation {
  z-index: 10001;
}

.tooltip-extrusion-header {
  font-family: inherit;
  font-size: 12px;
  .tooltip-inner {
    background-color: #4c4541;
    color: #fff;
    padding: 4px 8px;
    border-radius: 4px;
  }
}
</style>
